import React, { useEffect, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { GridDataCellTypes, IGridData, IGridDataCellActionButton, IModalObject } from '.';
import GridDataOptions from './GridDataOptions';
import { Avatar, Button, TextField } from '@mui/material';
import GridDataFilter from './GridDataFilter';
import GridLoadingPlaceholder from './GridLoadingPlaceholder';
import GenerateIcon from '../GenerateIcon';
import { useDispatch } from 'react-redux';
import { error } from 'console';
import { handleRequestError } from '../../services/api';
import GridDataActionButtons from './GridDataActionButtons';
import { ISearchParam, replaceItemDataInLink } from '../../features/BaseApp';
import GridDataCell from './GridDataCell';
import { useLocation } from 'react-router-dom';
import Thumbnail from '../Thumbnail';
import NothingFound from '../NothingFound';
import { isMobile } from "react-device-detect";


export const ThumbnailCellRenderer: React.FC<ICellRendererParams<IModalObject>> = ({ data }) => {
   
    return(
        <div className="grid-data-cell-thumbail-box">
            <Thumbnail src={data?.thumbnail} style={{ width: 80, height: 80 }} />
        </div>
    )
}

export default function GridData(props: IGridData) {

    const dispatch = useDispatch()
    const location = useLocation()

    const [currentPath, setCurrentPath] = useState<string>()

    const { 
        items, 
        columns, 
        config, 
        selectedItems,
        search,
        searchParams,
        loading,
        pagination,
        actionButtons,
        headerOptions,
        updateSearch,
        handleLoadItems,
        setSelectedItems,
        onDeleteItem,
        onDeleteManyItems,
        deleteItem,
        updateLoading,
        updateItemSearchParams,
        addNewItem,
        updateItem,
        updateAllItems,
        updateItemsPagination,
        updateSearchParamsBeforeLoadItems,
        forceRefresh, 
        forceRefreshStatus,
        nothingFoundMessage,
        addAdditionalFilters,
        renderAdditionalSection,
    } = props

    const gridRef = useRef<AgGridReact<IModalObject>>(null)

    const [filterShown, setFilterShown] = useState<boolean>(false)
    const hasLoaded = useRef(false)

    const [prevSearchParams, setPrevSearchParams] = useState<ISearchParam[]>([])
    const [enableLoadItemsOnSearchParamsChange, setEnableLoadItemsOnSearchParamsChange] = useState<boolean>(true)

    /**
     * Handle load items
     */
    let latestRequestId = 0;
    const loadItems = (page: number, search: string|null, disablePagination: boolean, addItems?: boolean, searchParams?: ISearchParam[]) => {
        const requestId = ++latestRequestId

        updateLoading(true)

        let searhParamsList: ISearchParam[] = []


        if (updateSearchParamsBeforeLoadItems){
            searhParamsList = updateSearchParamsBeforeLoadItems(searchParams ? searchParams : [])
        }

        if (!config.disableRemoveEmptySearchParams){
            if (searchParams){
                searhParamsList = searchParams.filter(
                    (param) => param.value !== null && param.value !== 0 && param.value !== false
                )
            }
        }

        setPrevSearchParams(searhParamsList)

        handleLoadItems(page, search, disablePagination, addItems, searhParamsList)
            .then(response => {

                if (requestId !== latestRequestId) {
                    console.log("[loadItems] Ignored outdated request");
                    return;
                }

                const { data } = response
                const results: IModalObject[] = disablePagination ? data : data.results

                if (addItems){
                    // results.map((singleItem) => addNewItem(singleItem, dispatch))
                    let updatedItems: IModalObject[] = [
                        ...items
                    ]

                    results.map((singleItem) => updatedItems.push(singleItem))
                    updateAllItems(updatedItems, dispatch)

                }else{
                    updateAllItems([], dispatch)
                    updateAllItems(results, dispatch)
                }
                
                if (!disablePagination){
                    updateItemsPagination({
                        count: data.count,
                        current_page: page,
                        total_pages: data.total_pages
                    }, dispatch)
                }else{
                    updateItemsPagination({
                        count: results.length,
                        current_page: 1,
                        total_pages: 1
                    }, dispatch)
                }

                updateLoading(false)
            })
            .catch(error => {
                updateLoading(false)
                handleRequestError(error, dispatch)
            })

    }

    /**
     * Handle search
     */
    const handleSearch = () => {
        loadItems(1, search ? search : "", false, false, searchParams)
    }

    /**
     * Update filter shown status
     */
    const handleUpdateFilterShowStatus = (status: boolean) => setFilterShown(status)

    /**
     * Handle on change selected
     */
    const handleSelectionChanged = () => {
        const selectedRows = gridRef.current?.api.getSelectedRows() ?? [];
        setSelectedItems(selectedRows)
    }

    /**
     * Load more items
     */
    const loadMoreItems = () => {
        if (pagination.current_page !== pagination.total_pages){
            loadItems(pagination.current_page + 1, search ? search : "", false, true, searchParams)
        }
    }

    /**
     * Load all
     */
    const loadAllItems = () => {
        if (pagination.current_page !== pagination.total_pages){
            loadItems(1, search ? search : "", true, true, searchParams)
        }
    }

    /**
     * Delete item
     */
    const handleDeleteItem = (id: number, permanentlyDelete: boolean = false) => {
        updateLoading(true)

        onDeleteItem(id, permanentlyDelete)
            .then(response => {
                deleteItem(id, dispatch)
                updateLoading(false)
            })
            .catch(error => {
                updateLoading(false)
                handleRequestError(error, dispatch)
            })
    }

    /**
     * Delete many items
     */
    const handleDeleteManyItems = (items: number[], permanentlyDelete: boolean = false) => {
        updateLoading(true)

        onDeleteManyItems(items, permanentlyDelete)
            .then(response => {
                updateLoading(false)
                items.map((item) => deleteItem(item, dispatch))
            })
            .catch(error => {
                updateLoading(false)
                handleRequestError(error, dispatch)
            })
    }

    /**
     * Refresh
     */
    const handleRefresh = () => {
        loadItems(pagination.current_page, search ? search : "", false, false, searchParams)
    }

    /**
     * Columns
     */
    let columnDefs: ColDef<IModalObject>[] = []

    /**
     * Select box
     */
    if (!config.disableSelect){
        columnDefs.push({
            headerCheckboxSelection: true,
            checkboxSelection: true,      
            width: 50,
            maxWidth: 50,
            minWidth: 50,
            pinned: 'left',
            resizable: false,
        })
    }

    columns.map((column) => columnDefs.push({
        cellRenderer: (params: ICellRendererParams<IModalObject>) => {
            return <GridDataCell {...params} type={(typeof column.type === "string" ? column.type : "text") as GridDataCellTypes} />
        },
        ...column,
    }))

    /**
     * Action buttons
     */
    if (!config.disableActionButtons){

        let defaultButtons: IGridDataCellActionButton[] = [
            {
                isIconButton: true,
                icon: "show",
                onClick: (data, dispatch, navigate)  =>{
                    navigate(replaceItemDataInLink(config.viewItemLink, data))
                },
            },
            {
                isIconButton: true,
                icon: "edit",
                onClick: (data, dispatch, navigate)  =>{
                    navigate(replaceItemDataInLink(config.editItemLink, data))
                },
            },
            {
                isIconButton: true,
                icon: "delete",
                color: "error",
                onClick: (data, dispatch, navigate)  =>{
                    handleDeleteItem(data.id)
                },
            }
        ]

        columnDefs.push({
            headerName: 'Actions',
            cellRenderer: GridDataActionButtons,
            cellRendererParams: {
              buttons: actionButtons.length > 0 ? actionButtons : defaultButtons
            },
            width: config.actionButtons?.width,
            pinned: config.actionButtons?.pinned,
        })
    }

    /**
     * Load items
     */
    useEffect(() => {
        if (!hasLoaded.current) {
            loadItems(1, search ?? "", false, false, searchParams)
            hasLoaded.current = true
          }
    }, [])

    /**
     * Refresh
     */
    useEffect(() => {
        if (forceRefresh && forceRefreshStatus){
            handleRefresh()
            forceRefresh(false)
        }
    }, [forceRefresh, forceRefreshStatus])

    const areEqualUnordered = (a: ISearchParam[], b: ISearchParam[]) => {
        if (a.length !== b.length) return false;

        const sortFn = (x: ISearchParam) => `${x.name}:${x.value}`;
        const sortedA = [...a].sort((x, y) => sortFn(x).localeCompare(sortFn(y)));
        const sortedB = [...b].sort((x, y) => sortFn(x).localeCompare(sortFn(y)));

        return sortedA.every((param, i) => 
            param.name === sortedB[i].name && param.value === sortedB[i].value
        );
    };

    /**
     * Submit on searchParams change
     */
    useEffect(() => {
        if (
            enableLoadItemsOnSearchParamsChange &&
            searchParams.length > 0 &&
            !areEqualUnordered(searchParams, prevSearchParams)
        ) {
            loadItems(pagination.current_page, search ?? "", false, false, searchParams);
        }
    }, [searchParams])

    /**
     * Return items
     */
    const returnItems = () => {
        if (config.hasSubItems && config.subItemsParamName) {
          return items.map(parent => ({
            ...parent,
            children: config.subItemsParamName ? parent[config.subItemsParamName] || [] : []
          }))
        }
        return items
    }

    // useEffect(() => {
    //     console.log("returnItems", JSON.stringify(returnItems(), null, 2));
    //   }, []);

    /**
     * Resest selected items when page is changed
     */
    useEffect(() => {
        if (currentPath !== location.pathname){
            setSelectedItems([])
            setCurrentPath(location.pathname)
        }
    }, [location.pathname])

    const NoRowsOverlay = () => {
        return <NothingFound title="Nothing found..." message={nothingFoundMessage} />
    }

    return (
        <div className="grid-data-container">
            <div className="content-of-grid-data-container">

                {!config.disableHeaderOptions && <GridDataOptions disableDelete={config.disableDelete} enableFilter={config.filterForm ? config.hideFilters ? false : true : false} filterShownStatus={filterShown} options={headerOptions ? headerOptions :[]} selectedItems={selectedItems} updateFilterShowStatus={handleUpdateFilterShowStatus} deleteItem={handleDeleteItem} deleteManyItems={handleDeleteManyItems} refresh={handleRefresh} />}

                {!config.disableSearch && (
                    <div className="grid-data-search-container">
                        <div className="content-of-grid-data-search-container">
                            <TextField 
                                variant="outlined" 
                                placeholder="Hit enter to search" 
                                label="Search" 
                                fullWidth 
                                value={search} 
                                onChange={(e) => updateSearch(e.target.value)} 
                                onKeyUp={(e) => e.key === "Enter" && handleSearch()} 
                            />
                            <Button className="general-button admin-button" variant="contained" onClick={() => handleSearch()}>Search</Button>
                        </div>
                    </div>
                )}

                {!config.hideFilters && config.filterForm?.name &&  <GridDataFilter shown={filterShown} form={config.filterForm} searchParams={searchParams} updateSearchParams={(params) => updateItemSearchParams(params)} addAdditionalFilters={addAdditionalFilters} enableLoadItemsOnSearchParamsChange={enableLoadItemsOnSearchParamsChange} setEnableLoadItemsOnSearchParamsChange={setEnableLoadItemsOnSearchParamsChange} />}
                {renderAdditionalSection && renderAdditionalSection()}

                <div className="ag-theme-alpine custom-font-grid" style={{ height: 500, width: '100%' }}>
                    
                    {selectedItems.length > 0 && (
                        <div className="grid-data-header-overlay">
                            <div className="content-of-grid-data-header-overlay">
                                <p>{selectedItems.length} Selected</p>
                            </div>
                        </div>
                    )}

                    <GridLoadingPlaceholder loading={loading} />

                    <AgGridReact 
                        ref={gridRef}
                        rowData={items}
                        columnDefs={columnDefs}
                        rowSelection="multiple"
                        suppressRowClickSelection={true}
                        // frameworkComponents={{ ThumbnailCellRenderer }}
                        rowHeight={config.rowHeight ? config.rowHeight : 100}
                        onSelectionChanged={handleSelectionChanged}
                        components={{
                            noRowsOverlay: NoRowsOverlay,
                        }}
                        noRowsOverlayComponent="noRowsOverlay"
                        suppressMovableColumns={isMobile}
                    />
                </div>

                <div className="load-more-of-grid-data-container">
                    <div className="content-of-load-more-of-grid-data-container">

                        <div className="text-box-of-load-more-of-grid-data-container">
                            <div className="content-of-text-box-of-load-more-of-grid-data-container">
                                <h4><span className="bold-text">Total:</span> {pagination.count}</h4>
                                <h4><span className="bold-text">Displayed:</span> {items.length}</h4>
                            </div>
                        </div>

                        <div className="buttons-of-load-more-of-grid-data-container">
                            <div className="content-of-buttons-of-load-more-of-grid-data-container">

                                <Button className="general-button admin-style" startIcon={<GenerateIcon icon="refresh" />} onClick={() => loadAllItems()} disabled={pagination.current_page === pagination.total_pages ? true : false}>Load all</Button>
                                <Button className="general-button admin-style" variant="contained" startIcon={<GenerateIcon icon="new" />} onClick={() => loadMoreItems()} disabled={pagination.current_page === pagination.total_pages ? true : false}>Load more</Button>

                            </div>
                        </div>

                    </div>
                </div>

            </div>
        </div>
    )
}